import { ConfigProvider, Flex } from "antd";
import { ITEM_TYPES, TabKeys } from "../../storyboardEditor/types";
import { useAntTheme } from "hooks/useAntTheme";

import { useTranslate } from "hooks/useTranslate/";
import { ThemeInfoTab } from "./ThemeInfoTab";
import { useTemplateEditorStore } from "../../storyboardEditor/store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../storyboardEditor/store/useTemplatesStatusStore";
import { StyledMenuTabs } from "../../storyboardEditor/components/StyledMenuTabs";
import { ShapesTab } from "../../storyboardEditor/components/StoryboardMenuTabs/ShapesTab";
import { ParameteredTextTab } from "./ParameteredTextTab";
import { PanelContent } from "../../storyboardEditor/components/StoryboardMenuTabs/PanelContent";
import { useTemplateData } from "../../storyboardEditor/hooks/useTemplateData";
import { ThemeBrandingTab } from "./ThemeBrandingTab";
import { ThemePresenterTab } from "./ThemePresenterTab";
import { ThemeMainImageTab } from "./ThemeMainImageTab";
import { TableTab } from "../../storyboardEditor/components/StoryboardMenuTabs/TableTab";
import { AnimationsTab } from "../../storyboardEditor/components/StoryboardMenuTabs/AnimationsTab";

export const ThemeEditorMenu = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const { theme } = useAntTheme();
  const t = useTranslate();

  const { brandKitData } = useTemplateData();

  const { brandKitPalette } = brandKitData;

  const { currentItem, setCurrentItem } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
    setCurrentItem: store.setCurrentItem,
  }));

  const { activeTab, setActiveTab } = useTemplatesStatusStore((state) => ({
    activeTab: state.activeTab,
    setActiveTab: state.setActiveTab,
  }));

  const tabsItems = [
    {
      key: TabKeys.themeInfo,
      label: t("media.themeEditor.themeInfo.tabTitle"),
      child: <ThemeInfoTab />,
    },
    {
      key: TabKeys.content,
      label: t("components.templates.settings.contentTab.tabTitle"),
      child: (
        <>
          <ParameteredTextTab
            previewHeight={previewHeight}
            previewWidth={previewWidth}
          />
          {currentItem?.type === ITEM_TYPES.Table && (
            <TableTab
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
          )}
        </>
      ),
    },
    {
      key: TabKeys.objects,
      label: t("components.templates.settings.objectsTab.tabTitle"),
      child: (
        <>
          <ShapesTab
            previewWidth={previewWidth}
            previewHeight={previewHeight}
            addToSlideLayout
            newShapeNamePrefix="slide_shape"
          />
          <ThemeMainImageTab
            previewWidth={previewWidth}
            previewHeight={previewHeight}
          />
        </>
      ),
    },
    {
      key: TabKeys.brand,
      label: t("components.templates.settings.brandTab.tabTitle"),
      child: (
        <ThemeBrandingTab
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      ),
    },
    // Not showing avatar tab for voice-only presenters (no face_profile)
    ...(brandKitPalette
      ? [
          {
            key: TabKeys.avatar,
            label: t("components.templates.settings.avatarTab.tabTitle"),
            child: (
              <ThemePresenterTab
                brandKitPalette={brandKitPalette}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
              />
            ),
          },
        ]
      : []),
    {
      key: TabKeys.motion,
      label: t("components.templates.settings.motionTab.tabTitle"),
      child: <AnimationsTab />,
    },
  ];

  const antTabsItems = tabsItems.map((item) => ({
    label: (
      <Flex
        vertical
        justify="center"
        align="center"
        style={{
          width: "100%",
          fontSize: 12,
        }}
      >
        {item.label}
      </Flex>
    ),
    key: `${item.key}`,
    children: <PanelContent>{item.child}</PanelContent>,
  }));

  return (
    <ConfigProvider
      theme={{
        components: {
          Tabs: {
            itemColor: theme.colorTextDescription,
            itemSelectedColor: theme.colorTextDescription,
            itemHoverColor: theme.colorPrimary,
            titleFontSize: 11,
            inkBarColor: "transparent",
          },
        },
      }}
    >
      <Flex
        vertical
        style={{
          width: "100%",
          height: "100%",
          // Solid surface so translucent colorBgContainerDisabled fills blend
          // over it (not the page), matching the left panel accordions.
          backgroundColor: theme.colorBgContainer,
        }}
      >
        <StyledMenuTabs
          tabPosition="left"
          items={antTabsItems}
          activeKey={activeTab ?? undefined}
          onChange={(tab) => {
            if (!isValidTabKey(tab)) return;

            setActiveTab(tab);
            if (tab === TabKeys.brand) {
              if (currentItem?.type !== "Logo") {
                setCurrentItem({ type: "Logo" });
              }
            } else if (tab === TabKeys.avatar) {
              if (currentItem?.type !== "Presenter") {
                setCurrentItem({ type: "Presenter" });
              }
            } else if (tab !== TabKeys.motion) {
              setCurrentItem(undefined);
            }
          }}
          tabBarGutter={0}
          tabBarStyle={{
            backgroundColor: theme.colorBgContainerDisabled,
          }}
          variant="card"
        />
      </Flex>
    </ConfigProvider>
  );
};

const isValidTabKey = (tab: string): tab is TabKeys => {
  return Object.values(TabKeys).includes(tab as TabKeys);
};
